import { useGetClientInviteCodeQuery } from '../../../redux/organization/organization.endpoints';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { websiteUrl } from 'src/constants/config';
import { Skeleton } from 'src/components/ui/skeleton';

interface InviteUserModalProps {
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const InviteUserModal = ({ clientId, isOpen, onClose }: InviteUserModalProps) => {
  const { data: adminInvite, isLoading: loadingAdmin } = useGetClientInviteCodeQuery({ clientId, forAdmin: true }, { skip: !isOpen });
  const { data: userInvite, isLoading: loadingUser } = useGetClientInviteCodeQuery({ clientId, forAdmin: false }, { skip: !isOpen });
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) setCopied(null);
  }, [isOpen]);

  const buildInviteLink = (inviteCode: string) =>
    `${websiteUrl}/create-account?invite_code=${inviteCode}&registration_type=client`;

  const handleCopy = (link: string, type: string) => {
    navigator.clipboard.writeText(link);
    setCopied(type);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header="Invite New User"
      style={{ width: '90vw', maxWidth: '500px' }}
      contentStyle={{ paddingLeft: 16, paddingRight: 16 }}
      draggable={false}
      resizable={false}
    >
      <div className="flex flex-col gap-6 w-full">
        <div>
          <div className="font-semibold my-1">
            Admin Invite Link
          </div>
          <div className="text-xs text-gray-500">
            Admin users can invite other users, edit their brand & logo, and view dashboards.
          </div>
          <div className="text-xs text-gray-500 mb-2">
            This invitation link expires in 24 hours.
          </div>
          <div className="flex flex-row items-center gap-2 w-full">
            {loadingAdmin ? (
              <Skeleton className="h-8 flex-1" />
            ) : adminInvite ? (
              <code className="break-all bg-gray-100 px-2 py-1 rounded text-xs flex-1 min-w-0">
                {buildInviteLink(adminInvite.invite_code)}
              </code>
            ) : (
              <div className="text-red-500 flex-1">
                Failed to load admin invite link.
              </div>
            )}
            <Button
              label={copied === 'admin' ? 'Copied!' : 'Copy'}
              icon={copied === 'admin' ? 'pi pi-check' : 'pi pi-copy'}
              onClick={() => handleCopy(buildInviteLink(adminInvite?.invite_code ?? ''), 'admin')}
              className="p-button-sm flex-shrink-0 flex flex-col items-center justify-center gap-1 text-xs [&>span]:m-0 w-auto"
              disabled={!adminInvite}
            />
          </div>
        </div>
        <div>
          <div className="font-semibold mb-1">
            Standard Invite Link
          </div>
          <div className="text-xs text-gray-500">
            Standard users can only view dashboards.
          </div>
          <div className="text-xs text-gray-500 mb-2">
            This invitation link expires in 7 days.
          </div>
          <div className="flex flex-row items-center gap-2 w-full">
            {loadingUser ? (
              <Skeleton className="h-8 flex-1" />
            ) : userInvite ? (
              <code className="break-all bg-gray-100 px-2 py-1 rounded text-xs flex-1 min-w-0">
                {buildInviteLink(userInvite.invite_code)}
              </code>
            ) : (
              <div className="text-red-500 flex-1">
                Failed to load user invite link.
              </div>
            )}
            <Button
              label={copied === 'user' ? 'Copied!' : 'Copy'}
              icon={copied === 'user' ? 'pi pi-check' : 'pi pi-copy'}
              onClick={() => handleCopy(buildInviteLink(userInvite?.invite_code ?? ''), 'user')}
              className="p-button-sm flex-shrink-0 flex flex-col items-center justify-center gap-1 text-xs [&>span]:m-0 w-auto"
              disabled={!userInvite}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};